import { useEffect, useState } from 'preact/hooks';
import type { EpisodeDto } from '../../shared/types';
import { api } from '../api';
import { EpisodeRow } from '../components/EpisodeRow';
import { LiveCarousel } from '../components/LiveCarousel';
import { SkeletonEpisodeList } from '../components/Skeleton';
import { isSignedIn, sessionLoading } from '../state/session';
import { SignInPanel } from '../components/SignInPanel';

export function Home() {
  const [continueListening, setContinueListening] = useState<EpisodeDto[]>([]);
  const [inbox, setInbox] = useState<EpisodeDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionLoading.value) return;
    if (!isSignedIn.value) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([api.continueListening(), api.inbox()])
      .then(([cont, box]) => {
        if (cancelled) return;
        setContinueListening(cont.episodes);
        /*
         * The two endpoints legitimately overlap: `/me/continue` returns everything started
         * and unfinished, `/me/inbox` returns the newest episodes across your subscriptions,
         * and an episode you began yesterday is in both. Left alone, Home draws the same
         * episode twice on one screen — once under "Continue listening" and again under
         * "Latest from your shows" — which is confusing on its own, and doubly so because
         * the two rows carry independent "add to queue" buttons for the same episode.
         *
         * De-duplicated here rather than server-side: "started" is not a reason for an
         * episode to vanish from a subscription feed, so the inbox endpoint is right to
         * return it. It is only *this screen* that should not say it twice, and Continue
         * listening wins because it is the more specific claim.
         */
        const resuming = new Set(cont.episodes.map((e) => e.id));
        setInbox(box.episodes.filter((e) => !resuming.has(e.id)));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSignedIn.value, sessionLoading.value]);

  // The session gate and the data fetch look the same to the reader — both are "the list is
  // not here yet" — so both get the list-shaped placeholder rather than a spinner that later
  // shoves the page around.
  if (sessionLoading.value) {
    return (
      <div class="view">
        <header class="view__header">
          <h1>Home</h1>
        </header>
        <SkeletonEpisodeList count={4} />
      </div>
    );
  }

  if (!isSignedIn.value) {
    return (
      <div class="view">
        <header class="view__header">
          <h1>Podcastie</h1>
          <p class="lede">
            A fast, open podcast client. Subscribe to any RSS feed, listen anywhere, and keep your
            position in sync across devices.
          </p>
        </header>
        <SignInPanel />
      </div>
    );
  }

  return (
    <div class="view">
      <header class="view__header">
        <h1>Home</h1>
      </header>

      <LiveCarousel />

      {loading && <SkeletonEpisodeList count={4} />}

      {!loading && continueListening.length === 0 && inbox.length === 0 && (
        <div class="empty">
          <h2>Nothing here yet</h2>
          <p>
            Find a show to get started — search by name, or paste a feed URL directly.
          </p>
          <a class="button" href="/search">
            Find podcasts
          </a>
        </div>
      )}

      {continueListening.length > 0 && (
        <section class="section">
          <h2 class="section__title">Continue listening</h2>
          <div class="episode-list">
            {continueListening.map((episode) => (
              <EpisodeRow key={episode.id} episode={episode} showPodcast />
            ))}
          </div>
        </section>
      )}

      {inbox.length > 0 && (
        <section class="section">
          <h2 class="section__title">Latest from your shows</h2>
          <div class="episode-list">
            {inbox.map((episode) => (
              <EpisodeRow key={episode.id} episode={episode} showPodcast />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
